import L from "leaflet";

// Offset multiplier for curve height based on distance (km)
const getOffsetScale = (distanceKm = 0) => {
  if (distanceKm < 25) return 0.08;
  if (distanceKm < 50) return 0.16;
  if (distanceKm < 100) return 0.32;
  if (distanceKm < 150) return 0.48;
  if (distanceKm < 250) return 0.7;
  if (distanceKm < 350) return 0.95;
  if (distanceKm < 450) return 1.2;
  if (distanceKm < 550) return 1.45;
  if (distanceKm < 650) return 1.7;
  if (distanceKm < 750) return 1.95;
  if (distanceKm < 850) return 2.2;
  if (distanceKm < 950) return 2.45;
  if (distanceKm < 1100) return 2.7;
  if (distanceKm < 1200) return 2.95;
  if (distanceKm < 1300) return 3.2;
  if (distanceKm < 2000) return 3.8;
  return 4.5;
};

const haversineKm = (from, to) => {
  const [lat1, lon1] = from;
  const [lat2, lon2] = to;
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const buildCurvePoints = (from, to, distanceKm) => {
  const latlngs = [from];

  const midLat = (from[0] + to[0]) / 2;
  const midLng = (from[1] + to[1]) / 2;
  const dx = to[1] - from[1];
  const dy = to[0] - from[0];
  const length = Math.sqrt(dx * dx + dy * dy) || 1;
  const offset = getOffsetScale(distanceKm);

  const control = [
    midLat + (-dy / length) * offset,
    midLng + (dx / length) * offset,
  ];

  for (let t = 0.02; t < 1; t += 0.02) {
    const lat =
      Math.pow(1 - t, 2) * from[0] +
      2 * (1 - t) * t * control[0] +
      Math.pow(t, 2) * to[0];
    const lng =
      Math.pow(1 - t, 2) * from[1] +
      2 * (1 - t) * t * control[1] +
      Math.pow(t, 2) * to[1];
    latlngs.push([lat, lng]);
  }

  latlngs.push(to);
  return latlngs;
};

function animatePolylineReveal(lines, fullLatLngs, duration, onDone) {
  if (!fullLatLngs.length) return null;

  const start = performance.now();
  let rafId = null;
  let finished = false;

  const tick = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    const count = Math.max(2, Math.ceil(fullLatLngs.length * eased));
    const slice = fullLatLngs.slice(0, count);
    lines.forEach((line) => line.setLatLngs(slice));

    if (progress < 1) {
      rafId = requestAnimationFrame(tick);
    } else if (!finished) {
      finished = true;
      if (onDone) onDone();
    }
  };

  rafId = requestAnimationFrame(tick);
  return () => {
    if (rafId) cancelAnimationFrame(rafId);
  };
}

export const createLineRenderer = (map, renderer = L.canvas({ padding: 0.5 })) => {
  /**
   * @param {[number, number]} from
   * @param {[number, number]} to
   * @param {string} color
   * @param {{ layerGroup?: L.LayerGroup, animate?: boolean, duration?: number, delay?: number, onComplete?: () => void }} [options]
   * @returns {{ layers: L.Layer[], cancelAnimation?: () => void }}
   */
  const drawLine = (from, to, color, options = {}) => {
    const {
      layerGroup = map,
      animate = false,
      duration = 700,
      delay = 0,
      onComplete,
    } = options;

    const distanceKm = haversineKm(from, to);
    const latlngs = buildCurvePoints(from, to, distanceKm);
    const initial = animate ? [latlngs[0], latlngs[0]] : latlngs;

    const solidLine = L.polyline(initial, {
      color,
      weight: 2.5,
      opacity: 0.95,
      lineCap: "round",
      smoothFactor: 0,
      noClip: true,
      renderer,
    }).addTo(layerGroup);

    const dashLine = L.polyline(initial, {
      color,
      weight: 6,
      opacity: animate ? 0 : 0.35,
      dashArray: "8 12",
      dashOffset: "0",
      lineCap: "round",
      smoothFactor: 0,
      noClip: true,
      renderer,
    }).addTo(layerGroup);

    const layers = [solidLine, dashLine];

    const startDot = L.circleMarker(from, {
      radius: animate ? 6 : 7,
      color,
      fillColor: color,
      fillOpacity: 1,
      weight: 0,
      renderer,
    }).addTo(layerGroup);
    layers.push(startDot);

    const endDot = L.circleMarker(to, {
      radius: animate ? 0 : 7,
      color,
      fillColor: color,
      fillOpacity: 1,
      weight: 0,
      renderer,
    }).addTo(layerGroup);
    layers.push(endDot);

    if (animate) {
      const growEndDot = () => {
        let r = endDot.getRadius();
        const grow = () => {
          r = Math.min(7, r + 0.55);
          endDot.setRadius(r);
          if (r < 7) requestAnimationFrame(grow);
        };
        requestAnimationFrame(grow);
      };
      window.setTimeout(growEndDot, delay + duration * 0.88);
    }

    let cancelAnimation = null;
    if (animate) {
      let revealCancel = null;
      const startReveal = () => {
        revealCancel = animatePolylineReveal([solidLine, dashLine], latlngs, duration, () => {
          dashLine.setStyle({ opacity: 0.35 });
          if (onComplete) onComplete();
        });
      };
      if (delay > 0) {
        const timeoutId = window.setTimeout(startReveal, delay);
        cancelAnimation = () => {
          window.clearTimeout(timeoutId);
          revealCancel?.();
        };
      } else {
        startReveal();
        cancelAnimation = () => revealCancel?.();
      }
    }

    return { layers, cancelAnimation };
  };

  return { drawLine, renderer };
};
